import React, { useEffect, useState } from 'react';
import { useColorModeValue, useColorMode } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

function SalesInvoiceTabs({ handleSalesInvoiceClick }) {
   const params = useParams();
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   const [daynamicTab, setDaynamicTab] = useState([]);
   const [selectedTabId, setSelectedTabId] = useState('invoice');
   const [isInitialized, setIsInitialized] = useState(false);
   const daybookData = useSelector((state) => state.daybook?.list || []);
   const daybookTypeData = daybookData.filter((dy) => dy.type === 5);

   // Color scheme based on theme
   const containerBg = isDark ? 'bg-gray-900' : 'bg-gray-50';
   const selectedTabBg = isDark ? 'bg-blue-900' : 'bg-green-50';
   const selectedBorderColor = isDark ? 'border-blue-500' : 'border-green-500';
   const selectedTextColor = isDark ? 'text-white' : 'text-gray-900';
   const inactiveTabBg = isDark ? 'bg-gray-800' : 'bg-white';
   const inactiveTextColor = isDark ? 'text-gray-400' : 'text-gray-600';
   const countBg = isDark ? 'bg-gray-700' : 'bg-gray-200';
   const countTextColor = isDark ? 'text-gray-200' : 'text-gray-700';
   const amountTextColor = isDark ? 'text-blue-300' : 'text-blue-600';

   // Get user and company info from localStorage
   const getUserCompanyKey = () => {
      const companyId = localStorage.getItem('companyId');
      const email = localStorage.getItem('email');
      return `selectedSalesInvoiceTabId_${companyId}_${email}`;
   };

   const tab = {
      label: 'All',
      value: 'invoice',
      id: 'invoice',
      path: 'sales/invoice',
   };

   const dynamicTabs =
      daybookTypeData?.filter((dy) => dy.totalSales !== 0).map((item) => ({
         label: item.name,
         count: item.totalSales,
         amount: item.bal,
         value: `invoice/daybook/${item.id}`,
         id: `${item.id}`,
         path: `sales/invoice/daybook/${item.id}`,
      })) || [];

   const tabs = [tab, ...dynamicTabs];

   // Effect to handle tab restoration and initialization
   useEffect(() => {
      if (!tabs.length || !localStorage.getItem('companyId') || !localStorage.getItem('email')) {
         return;
      }

      const userCompanyKey = getUserCompanyKey();
      const savedTabId = localStorage.getItem(userCompanyKey);
      let targetTabId = 'invoice';
      let targetTabValue = 'invoice';

      if (savedTabId !== null) {
         const savedTab = tabs.find((tab) => tab.id === savedTabId);

         if (savedTab) {
            targetTabId = savedTabId;
            targetTabValue = savedTab.value;
         } else {
            targetTabId = 'invoice';
            targetTabValue = 'invoice';
         }
      } else {
         const currentPath = params['*']?.split('/')[3] || params['*']?.split('/')[1] || 'invoice';
         const pathBasedTab = tabs.find((tab) => tab.id === currentPath);

         if (pathBasedTab) {
            targetTabId = currentPath;
            targetTabValue = pathBasedTab.value;
         }

         localStorage.setItem(userCompanyKey, targetTabId);
      }

      setSelectedTabId(targetTabId);

      if (!isInitialized) {
         handleSalesInvoiceClick(targetTabValue);
         setIsInitialized(true);
      }
   }, [params, tabs, isInitialized]);

   // Handle tab click
   const handleTabClick = (tabValue, tabId) => {
      const userCompanyKey = getUserCompanyKey();
      setSelectedTabId(tabId);
      localStorage.setItem(userCompanyKey, tabId);
      handleSalesInvoiceClick(tabValue);
   };

   return (
      <div className={` overflow-x-auto py-3 ${containerBg} rounded-lg`}>
         <div className="relative px-3">
            <div className="flex gap-3 min-w-max">
               {tabs.map((tab, index) => {
                  const isSelected = selectedTabId === tab.id;
                  const hasDetails = tab.count !== undefined && tab.amount !== undefined;

                  return (
                     <button
                        key={index}
                        className={`
                           relative font-medium text-sm rounded-md px-4 py-2 transition-all duration-300 
                           whitespace-nowrap border-2 group
                           ${isSelected
                              ? `${selectedTabBg} ${selectedBorderColor} ${selectedTextColor} shadow-lg transform scale-105`
                              : `${inactiveTabBg} border-transparent ${inactiveTextColor} shadow-md hover:shadow-lg hover:scale-102`
                           }
                        `}
                        onClick={() => handleTabClick(tab.value, tab.id)}>
                        <div className="flex flex-col items-start  w-full">
                           {/* Tab Label */}
                           <div className="flex items-center justify-between w-full gap-3">
                              <span className={`font-bold text-base ${isSelected ? 'text-current' : 'text-inherit'}`}>
                                 {tab.label}
                              </span>
                              {hasDetails && (
                                 <span
                                    className={`px-3 py-1 rounded-full text-xs font-bold ${countBg} ${countTextColor} shadow-sm`}>
                                    {tab.count}
                                 </span>
                              )}
                              {hasDetails && (
                                 <div
                                    className={`flex items-center justify-between w-full transition-all duration-300 ${isSelected ? 'opacity-100' : 'opacity-80'
                                       }`}>
                                    <span
                                       className={`
                                    text-sm font-bold px-2 py-1 rounded
                                    ${isSelected
                                             ? isDark
                                                ? 'bg-blue-700 text-blue-100'
                                                : 'bg-green-200 text-green-800'
                                             : amountTextColor
                                          }
                                 `}>
                                       ₹{tab.amount?.toLocaleString() || '0'}
                                    </span>
                                 </div>
                              )}
                           </div>
                        </div>

                        {/* Hover effect */}
                        {!isSelected && (
                           <div className="absolute inset-0 rounded-xl bg-gradient-to-b from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        )}
                     </button>
                  );
               })}
            </div>
         </div>

         {/* Custom scrollbar styling */}
         <style jsx>{`
            div::-webkit-scrollbar {
               height: 6px;
            }
            div::-webkit-scrollbar-track {
               background: transparent;
            }
            div::-webkit-scrollbar-thumb {
               background: ${isDark ? '#4a5568' : '#cbd5e0'};
               border-radius: 10px;
            }
            div::-webkit-scrollbar-thumb:hover {
               background: ${isDark ? '#718096' : '#a0aec0'};
            }
         `}</style>
      </div>
   );
}

export default SalesInvoiceTabs;
